import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';

export default function MergeCustomersModal({ primaryId, secondaryId, onClose, onMerged, t }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPrimary, setSelectedPrimary] = useState(primaryId);
  const [merging, setMerging] = useState(false);
  const [result, setResult] = useState(null);

  const mg = t.admin?.merge || {};

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/customers/merge/preview?primary_id=${primaryId}&secondary_id=${secondaryId}`);
        setPreview(res.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load preview');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [primaryId, secondaryId]);

  const handleMerge = async () => {
    if (!window.confirm(mg.confirmWarning || 'This action cannot be undone. Are you sure?')) return;
    setMerging(true);
    try {
      const pId = selectedPrimary;
      const sId = selectedPrimary === primaryId ? secondaryId : primaryId;
      const res = await axios.post(`${API}/admin/customers/merge`, { primary_id: pId, secondary_id: sId });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Merge failed');
      setMerging(false);
    }
  };

  const customerCard = (cust, isPrimary) => (
    <div style={{
      flex: '1 1 0', padding: '1rem', background: isPrimary ? 'rgba(212,43,43,0.08)' : '#111827',
      border: `1px solid ${isPrimary ? 'rgba(212,43,43,0.4)' : 'rgba(148,163,184,0.15)'}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <input
          type="radio"
          name="primary"
          checked={selectedPrimary === cust.id}
          onChange={() => setSelectedPrimary(cust.id)}
          style={{ accentColor: '#D42B2B' }}
        />
        <span style={{ fontSize: '0.7rem', color: selectedPrimary === cust.id ? '#D42B2B' : '#64748B', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          {selectedPrimary === cust.id ? (mg.primary || 'Primary — keeps identity') : (mg.secondary || 'Secondary — will be deactivated')}
        </span>
      </div>
      <div style={{ color: '#E8ECF0', fontSize: '1rem', marginBottom: '0.5rem' }}>{cust.first_name} {cust.last_name}</div>
      <div style={{ fontSize: '0.8rem', color: '#94A3B8', marginBottom: '0.2rem' }}>{cust.email}</div>
      <div style={{ fontSize: '0.8rem', color: '#94A3B8', marginBottom: '0.2rem' }}>{cust.phone || '—'} {cust.phone2 ? `/ ${cust.phone2}` : ''}</div>
      <div style={{ fontSize: '0.75rem', color: '#64748B', marginBottom: '0.75rem' }}>
        {mg.created || 'Created'}: {new Date(cust.created_at).toLocaleDateString()}
      </div>
      {cust.counts && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.3rem', fontSize: '0.75rem' }}>
          {Object.entries(cust.counts).filter(([, v]) => v > 0).map(([k, v]) => (
            <div key={k} style={{ color: '#94A3B8' }}>
              <span style={{ color: '#E8ECF0' }}>{v}</span> {mg[k] || k}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }} onClick={onClose}>
      <div className="modal-dialog modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-content" style={{ background: '#0C1220', border: '1px solid rgba(148,163,184,0.2)', color: '#E8ECF0' }}>
          <div className="modal-header" style={{ borderBottom: '1px solid rgba(148,163,184,0.15)' }}>
            <h5 className="modal-title" style={{ fontFamily: "'Rajdhani', sans-serif", color: '#D42B2B' }}>
              {mg.title || 'Merge Customer Accounts'}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {loading && <div style={{ textAlign: 'center', padding: '2rem', color: '#D42B2B' }}>{t.common?.loading || 'Loading...'}</div>}
            {error && !result && <div className="alert alert-danger" style={{ fontSize: '0.85rem' }}>{error}</div>}

            {result ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '1.2rem', color: '#22c55e', marginBottom: '1rem' }}>{mg.success || 'Accounts merged successfully!'}</div>
                <div style={{ fontSize: '0.85rem', color: '#94A3B8', marginBottom: '1.5rem' }}>
                  {Object.entries(result.merged).filter(([, v]) => v > 0).map(([k, v]) => (
                    <span key={k} style={{ marginRight: '1rem' }}>{v} {k}</span>
                  ))}
                </div>
                <button className="btn btn-primary" onClick={() => { onMerged(); onClose(); }}>{t.common?.close || 'Close'}</button>
              </div>
            ) : preview && (
              <>
                <p style={{ fontSize: '0.85rem', color: '#94A3B8', marginBottom: '1rem' }}>
                  {mg.selectPrimary || 'Select which account to keep. All data from the other account will be transferred.'}
                </p>
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                  {customerCard(preview.primary, selectedPrimary === preview.primary.id)}
                  {customerCard(preview.secondary, selectedPrimary === preview.secondary.id)}
                </div>
                <div style={{ padding: '0.75rem', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', fontSize: '0.8rem', color: '#f87171', marginBottom: '1rem' }}>
                  {mg.warning || 'This action cannot be undone. The secondary account will be permanently deactivated.'}
                </div>
              </>
            )}
          </div>
          {preview && !result && (
            <div className="modal-footer" style={{ borderTop: '1px solid rgba(148,163,184,0.15)' }}>
              <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>{mg.cancel || 'Cancel'}</button>
              <button className="btn btn-danger btn-sm" onClick={handleMerge} disabled={merging}>
                {merging ? (t.common?.loading || 'Merging...') : (mg.confirm || 'Confirm Merge')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
